import { AuthService } from '../authService';

const readJson = async <T>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error ? `${payload.error}${payload.details ? ` ${payload.details}` : ''}` : `Voice request mislukt (${response.status}).`);
  }
  return payload as T;
};

// Maakt een Error met extra metadata over rate-limit / retry.
const throwBosonError = async (response: Response, fallbackMessage: string) => {
  const payload = await response.json().catch(() => null);
  const retryAfterHeader = response.headers.get('Retry-After');
  const retryAfterSec = retryAfterHeader ? Number(retryAfterHeader) * 1000 : undefined;
  const retryAfterMs = payload?.retryAfterMs ?? retryAfterSec;
  const err: any = new Error(payload?.error || fallbackMessage);
  err.code = payload?.code;
  err.retryAfterMs = retryAfterMs;
  err.status = response.status;
  throw err;
};

// Helper: doe een POST en als het 429/rate_limit is, retry 1x na wachttijd.
const postWithRateLimitRetry = async <T>(
  path: string,
  body: unknown,
  parse: (response: Response) => Promise<T>,
  maxRetries = 1,
): Promise<T> => {
  let attempt = 0;
  while (true) {
    const response = await AuthService.authFetch(path, {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify(body),
    });
    if (response.ok) return await parse(response);
    if (response.status === 429 && attempt < maxRetries) {
      const payload = await response.json().catch(() => null);
      const waitMs = Number(payload?.retryAfterMs || response.headers.get('Retry-After') || 5000);
      await new Promise((r) => setTimeout(r, Math.min(waitMs, 60_000)));
      attempt++;
      continue;
    }
    throw await throwBosonError(response, 'Voice request mislukt.');
  }
};

export class VoiceStackService {
  static async synthesizeSpeech(input: {
    text: string;
    provider?: 'auto' | 'elevenlabs' | 'supertonic' | 'voxcpm' | 'boson-higgs';
    voiceId?: string;
    language?: string;
    voiceStyle?: string;
    voiceDesignPrompt?: string;
    cfgValue?: number;
    inferenceTimesteps?: number;
    boson?: { voice?: string; refAudio?: string; refText?: string; temperature?: number; topK?: number; stream?: boolean };
  }, signal?: AbortSignal): Promise<{ audio: ArrayBuffer; provider: string; latencyMs: number }> {
    // Voor niet-Boson requests gebruik de directe authFetch (geen retry nodig).
    // Voor Boson-traceable requests (provider === 'boson-higgs' of 'auto') retry 1x op 429.
    const bosonLikely = !input.provider || input.provider === 'auto' || input.provider === 'boson-higgs';
    if (!bosonLikely) {
      const response = await AuthService.authFetch('/voice/stack/speech', {
        method: 'POST',
        headers: AuthService.authHeaders(true),
        body: JSON.stringify(input),
        signal,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error ? `${payload.error}${payload.details ? ` ${payload.details}` : ''}` : 'Voice speech mislukt.');
      }
      return {
        audio: await response.arrayBuffer(),
        provider: response.headers.get('X-BDAT-Voice-Provider') || input.provider || 'auto',
        latencyMs: Number(response.headers.get('X-BDAT-Voice-Latency-Ms') || '0'),
      };
    }
    return postWithRateLimitRetry(
      '/voice/stack/speech',
      input,
      async (response) => ({
        audio: await response.arrayBuffer(),
        provider: response.headers.get('X-BDAT-Voice-Provider') || input.provider || 'auto',
        latencyMs: Number(response.headers.get('X-BDAT-Voice-Latency-Ms') || '0'),
      }),
    );
  }

  static async synthesizeElevenLabs(text: string, voiceId?: string, signal?: AbortSignal): Promise<ArrayBuffer> {
    const response = await AuthService.authFetch('/voice/stack/elevenlabs/stream', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify({ text, voiceId }),
      signal,
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error ? `${payload.error}${payload.details ? ` ${payload.details}` : ''}` : 'ElevenLabs TTS mislukt.');
    }

    return response.arrayBuffer();
  }

  static async generateTurn(input: {
    apiKey?: string;
    model: string;
    systemPrompt: string;
    prompt: string;
    signal?: AbortSignal;
  }): Promise<{ text: string; model: string }> {
    const { signal, ...payload } = input;
    const response = await AuthService.authFetch('/voice/stack/openrouter/turn', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify(payload),
      signal,
    });
    return readJson<{ text: string; model: string }>(response);
  }

  static async checkDeepgram(): Promise<{ ok: boolean; degraded?: boolean; message?: string; error?: string }> {
    const response = await AuthService.authFetch('/voice/stack/deepgram/status', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify({}),
    });
    return readJson(response);
  }

  static async getDeepgramToken(): Promise<{ accessToken: string; expiresIn: number }> {
    const response = await AuthService.authFetch('/voice/stack/deepgram/token', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify({}),
    });
    return readJson(response);
  }

  static async transcribeDeepgramAudio(audio: Blob): Promise<{ transcript: string; confidence: number }> {
    const response = await AuthService.authFetch('/voice/stack/deepgram/transcribe', {
      method: 'POST',
      headers: AuthService.authHeaders(false),
      body: audio,
    });
    return readJson(response);
  }

  static async testVoxcpm(input: { text?: string; voiceDesignPrompt?: string; voiceStyle?: string } = {}): Promise<{ audio: ArrayBuffer; provider: string; latencyMs: number }> {
    const response = await AuthService.authFetch('/voice/stack/voxcpm/test', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error ? `${payload.error}${payload.details ? ` ${payload.details}` : ''}` : 'VoxCPM2 test mislukt.');
    }

    return {
      audio: await response.arrayBuffer(),
      provider: response.headers.get('X-BDAT-Voice-Provider') || 'voxcpm',
      latencyMs: Number(response.headers.get('X-BDAT-Voice-Latency-Ms') || '0'),
    };
  }

  static async testBoson(input: { text?: string; voice?: string } = {}): Promise<{ audio: ArrayBuffer; provider: string; latencyMs: number }> {
    return postWithRateLimitRetry(
      '/voice/stack/boson/test',
      input,
      async (response) => ({
        audio: await response.arrayBuffer(),
        provider: response.headers.get('X-BDAT-Voice-Provider') || 'boson-higgs',
        latencyMs: Number(response.headers.get('X-BDAT-Voice-Latency-Ms') || '0'),
      }),
    );
  }

  // Test een opgeslagen custom voice (voice_id) via /boson/test endpoint.
  static async testCustomBosonVoice(input: { voiceId: string; text?: string }): Promise<{ audio: ArrayBuffer; provider: string; latencyMs: number }> {
    return postWithRateLimitRetry(
      '/voice/stack/boson/test',
      { voice: input.voiceId, text: input.text || 'Hallo, dit is een test van mijn gekloonde stem.' },
      async (response) => ({
        audio: await response.arrayBuffer(),
        provider: response.headers.get('X-BDAT-Voice-Provider') || 'boson-higgs',
        latencyMs: Number(response.headers.get('X-BDAT-Voice-Latency-Ms') || '0'),
      }),
    );
  }

  static async checkBoson(): Promise<{ configured: boolean; healthy: boolean; latencyMs: number; error?: string }> {
    const response = await AuthService.authFetch('/voice/stack/boson/health', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify({}),
    });
    return readJson(response);
  }
}
